/* ENSSA Reporting — chart primitives.
   Risk colours from CSS tokens. Charts handle the four-state risk schema. */

import React from "react";
import { Users } from "lucide-react";
import { RISK, RISK_ORDER } from "@/data/tokens";
import { domainRiskLabel, taskBandLabel } from "@/lib/copy";
import {
  buildDensityPath,
  CHART_AXIS,
  DISTRIBUTION_FILL,
  type PositionScope,
} from "@/lib/charts";
import {
  HIGH_BAND_MAX_INDEX,
  MODERATE_BAND_MAX_INDEX,
  STANDARD_SCORE_AXIS_MAX,
  STANDARD_SCORE_AXIS_MIN,
} from "@/lib/score-bands";
import { useMediaQuery } from "@/lib/useMediaQuery";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { RiskCounts, TaskBoxStat, TaskFamilyBand } from "@/types";

interface RiskBreakdownStackedBarProps {
  counts: RiskCounts;
  height?: number;
  showLegend?: boolean;
  showLabels?: boolean;
}

/* ─── Risk breakdown stacked bar ─────────────────────────── */
function RiskBreakdownStackedBar({ counts, height = 18, showLegend = true, showLabels = false }: RiskBreakdownStackedBarProps) {
  const total = counts.low + counts.moderate + counts.high + counts.more_info || 1;
  const segs = RISK_ORDER.map((k) => ({ key: k, n: counts[k] || 0, pct: (counts[k] || 0) / total * 100 }));
  return (
    <div>
      <div style={{ display: 'flex', height, borderRadius: 999, overflow: 'hidden', background: 'var(--ink-100)', border: '1px solid var(--border-subtle)' }}>
        {segs.map((s) => s.n > 0 &&
        <div key={s.key} title={`${domainRiskLabel(s.key)}: ${s.n}`}
        style={{
          width: `${s.pct}%`,
          background: s.key === 'more_info' ? 'repeating-linear-gradient(45deg, var(--ink-200) 0 6px, var(--ink-100) 6px 12px)' : RISK[s.key].color,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: s.key === 'more_info' ? 'var(--fg-2)' : 'var(--ink-0)',
          fontSize: 11, fontWeight: 700
        }}>
            {showLabels && s.pct > 8 ? s.n : null}
          </div>
        )}
      </div>
      {showLegend &&
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, marginTop: 10, fontSize: 12, color: 'var(--fg-2)' }}>
          {RISK_ORDER.map((k) =>
        <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span style={{
            width: 10, height: 10, borderRadius: 2,
            background: k === 'more_info' ? 'repeating-linear-gradient(45deg, var(--ink-200) 0 3px, var(--ink-100) 3px 6px)' : RISK[k].color,
            border: '1px solid var(--border-default)'
          }} />
              <strong style={{ fontFamily: 'var(--font-mono)', color: 'var(--fg-1)' }}>{counts[k] || 0}</strong>{' '}
              {domainRiskLabel(k).toLowerCase()}
            </span>
        )}
        </div>
      }
    </div>);

}

interface TaskBoxPlotPanelProps {
  stats: Record<string, TaskBoxStat>;
  compact?: boolean;
  onRowExpanded?: (taskId: string) => void;
}

/* ─── Task box plot panel ────────────────────────────────── */
function TaskBoxPlotPanel({ stats, compact = false, onRowExpanded }: TaskBoxPlotPanelProps) {
  const isNarrow = useMediaQuery('(max-width: 640px)');
  const desktopCols = compact ? '140px 1fr 200px' : '170px 1fr 220px';
  const narrowCols = 'minmax(110px, 36%) 1fr';
  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: isNarrow ? narrowCols : desktopCols, gap: isNarrow ? 12 : 16, alignItems: 'center', padding: '6px 0 14px', borderBottom: '1px solid var(--border-subtle)', fontSize: 11, fontWeight: 700, color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
        <div style={{ textAlign: 'center' }}>Task</div>
        <div style={{ textAlign: 'center' }}>{isNarrow ? 'DISTRIBUTION' : 'PERCENTILE DISTRIBUTION'}</div>
        {!isNarrow && <div style={{ textAlign: 'center' }}>STATS</div>}
      </div>
      {Object.values(stats).map((s) => <BoxPlotRow key={s.id} s={s} compact={compact} isNarrow={isNarrow} onRowExpanded={onRowExpanded} />)}
    </div>);

}

interface BoxPlotTip {
  label: string;
  pct: number;
}

interface BoxPlotRowProps {
  s: TaskBoxStat;
  compact: boolean;
  isNarrow: boolean;
  onRowExpanded?: (taskId: string) => void;
}

function BoxPlotRow({ s, compact, isNarrow, onRowExpanded }: BoxPlotRowProps) {
  const W = 100;
  const [tip, setTip] = React.useState<BoxPlotTip | null>(null);
  const [expanded, setExpanded] = React.useState(false);
  const desktopCols = compact ? '140px 1fr 200px' : '170px 1fr 220px';
  const narrowCols = 'minmax(110px, 36%) 1fr';

  const toggleExpanded = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isNarrow) return;
    // Don't toggle if user was hovering/clicking on the box plot (tooltip targets)
    if ((e.target as Element).closest('svg')) return;
    setExpanded((value) => {
      const nextValue = !value;
      if (nextValue) onRowExpanded?.(s.id);
      return nextValue;
    });
  };

  const statsBlock = (
    <div style={{ textAlign: isNarrow ? 'left' : 'right', fontSize: 12, lineHeight: 1.65, color: 'var(--fg-2)', fontFamily: 'var(--font-mono)' }}>
      <div>Median <strong>{s.p50}PR</strong> · IQR {s.p25}–{s.p75}PR</div>
      <div>
        {s.below20 > 0 && <span style={{ color: 'var(--risk-concern-fg)', fontWeight: 700 }}>{s.below20} below 20PR · </span>}
        <span style={{ color: 'var(--fg-3)' }}>n={s.n}</span>
      </div>
    </div>
  );

  return (
    <div style={{ borderBottom: '1px solid var(--border-subtle)' }}>
      <div onClick={toggleExpanded}
        style={{ display: 'grid', gridTemplateColumns: isNarrow ? narrowCols : desktopCols, gap: isNarrow ? 12 : 16, alignItems: 'center', padding: '14px 0', cursor: isNarrow ? 'pointer' : 'default' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          {isNarrow && (
            <span aria-hidden="true" style={{ fontSize: 11, color: 'var(--fg-3)', flexShrink: 0, marginTop: 2 }}>{expanded ? '▾' : '▸'}</span>
          )}
          <div style={{ fontWeight: 700, fontSize: isNarrow ? 13 : 14, lineHeight: 1.3, whiteSpace: 'normal', wordBreak: 'break-word', flex: 1 }}>{s.label}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 9, color: 'var(--fg-3)', fontFamily: 'var(--font-mono)', flexShrink: 0 }}>0PR</span>
          <div style={{ flex: 1, position: 'relative', height: 28 }}>
            {tip && (
              <div style={{
                position: 'absolute', left: `${tip.pct}%`, bottom: 'calc(100% + 6px)',
                transform: 'translateX(-50%)',
                background: 'var(--ink-800)', color: 'var(--ink-0)',
                fontSize: 11, fontWeight: 700, padding: '4px 9px', borderRadius: 6,
                whiteSpace: 'nowrap', pointerEvents: 'none', zIndex: 10,
                boxShadow: 'var(--sh-2)',
              }}>
                {tip.label}
                <span style={{ position: 'absolute', left: '50%', bottom: -4, transform: 'translateX(-50%)', width: 8, height: 8, background: 'var(--ink-800)', clipPath: 'polygon(0 0,100% 0,50% 100%)' }} />
              </div>
            )}
            <svg viewBox={`0 0 ${W} 28`} preserveAspectRatio="none"
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', overflow: 'visible' }}>
              <line x1={0} x2={0} y1={2} y2={26} stroke="var(--ink-150)" strokeWidth="0.5" />
              <line x1={100} x2={100} y1={2} y2={26} stroke="var(--ink-150)" strokeWidth="0.5" />
              <line x1={s.p10} x2={s.p90} y1={14} y2={14} stroke="var(--ink-300)" strokeWidth="0.6" />
              <line x1={s.p10} x2={s.p10} y1={9}  y2={19} stroke="var(--ink-300)" strokeWidth="0.6"
                style={{ cursor: 'pointer' }}
                onMouseEnter={() => setTip({ label: `${s.p10}PR`, pct: s.p10 })}
                onMouseLeave={() => setTip(null)} />
              <line x1={s.p90} x2={s.p90} y1={9}  y2={19} stroke="var(--ink-300)" strokeWidth="0.6"
                style={{ cursor: 'pointer' }}
                onMouseEnter={() => setTip({ label: `${s.p90}PR`, pct: s.p90 })}
                onMouseLeave={() => setTip(null)} />
              {/* IQR box — hoverable */}
              <rect x={s.p25} y={6} width={Math.max(0.6, s.p75 - s.p25)} height={16}
                fill="var(--enssa-mauve)" opacity="0.38" stroke="var(--enssa-aubergine)" strokeWidth="0.5"
                style={{ cursor: 'pointer' }}
                onMouseEnter={() => setTip({ label: `IQR: ${s.p25}PR to ${s.p75}PR`, pct: (s.p25 + s.p75) / 2 })}
                onMouseLeave={() => setTip(null)} />
              {/* Invisible wide hit area for median */}
              <line x1={s.p50} x2={s.p50} y1={4} y2={24} stroke="transparent" strokeWidth="8"
                style={{ cursor: 'pointer' }}
                onMouseEnter={() => setTip({ label: `Median: ${s.p50}PR`, pct: s.p50 })}
                onMouseLeave={() => setTip(null)} />
              <line x1={s.p50} x2={s.p50} y1={4} y2={24} stroke="var(--enssa-aubergine)" strokeWidth="0.9"
                style={{ pointerEvents: 'none' }} />
            </svg>
          </div>
          <span style={{ fontSize: 9, color: 'var(--fg-3)', fontFamily: 'var(--font-mono)', flexShrink: 0 }}>100PR</span>
        </div>
        {!isNarrow && statsBlock}
      </div>
      {isNarrow && expanded && (
        <div style={{ background: 'var(--ink-50)', padding: '10px 12px 14px', borderTop: '1px solid var(--border-subtle)' }}>
          {statsBlock}
        </div>
      )}
    </div>
  );
}

interface TaskFamilyDistributionBarProps {
  /** Counts per band. */
  veryLow: number;
  low: number;
  onTrack: number;
  limited: number;
  notAttempted?: number;
  height?: number;
  /** Inline percentage row beneath the bar (percentage + label pairs). */
  showCounts?: boolean;
  /** Students per band. When provided each segment is clickable and
      shows a "click to see students" prompt on hover. */
  members?: Record<TaskFamilyBand, { id: string; name: string }[]>;
  /** Called when a segment is clicked — receives the band and its students. */
  onBandClick?: (band: TaskFamilyBand, students: { id: string; name: string }[]) => void;
}

const TASK_BAND_FILL: Record<TaskFamilyBand, string> = {
  very_low: 'var(--risk-concern)',
  low: 'var(--risk-emerging)',
  on_track: 'var(--risk-on-track)',
  limited: 'var(--task-limited)',
  not_attempted: 'var(--task-limited-bg)',
};

const TASK_NOT_ATTEMPTED_HATCH =
  'repeating-linear-gradient(45deg, var(--ink-200) 0 4px, var(--task-limited-bg) 4px 8px)';

/* Tooltip body — band label + count + click prompt. */
function BandTooltipBody({ band, n, pct }: { band: TaskFamilyBand; n: number; pct: number }) {
  return (
    <div style={{ lineHeight: 1.45 }}>
      <div style={{ fontWeight: 800, marginBottom: 3 }}>
        {taskBandLabel(band)} · {n} student{n !== 1 ? 's' : ''} ({pct}%)
      </div>
      <div style={{ opacity: 0.85, fontSize: 11 }}>Click to see list</div>
    </div>
  );
}

/* ─── Task-family distribution bar ───────────────────────────
   One contiguous stacked bar across comparable bands plus neutral
   evidence states. Limited evidence and not-attempted are evidence/admin
   states, not performance bands. */
function TaskFamilyDistributionBar({
  veryLow, low, onTrack, limited, notAttempted = 0, height = 22, members, onBandClick,
}: TaskFamilyDistributionBarProps) {
  const countOf = (b: TaskFamilyBand) =>
    b === 'very_low' ? veryLow : b === 'low' ? low : b === 'on_track' ? onTrack : b === 'limited' ? limited : notAttempted;
  const segs: TaskFamilyBand[] = ['very_low', 'low', 'on_track', 'limited', 'not_attempted'];
  const total = segs.reduce((sum, b) => sum + countOf(b), 0) || 1;
  const pctOf = (b: TaskFamilyBand) => Math.round((countOf(b) / total) * 100);
  const isInteractive = !!(members && onBandClick);
  const segmentFill = (b: TaskFamilyBand) =>
    b === 'not_attempted' ? TASK_NOT_ATTEMPTED_HATCH : TASK_BAND_FILL[b];
  const segmentTextColor = (b: TaskFamilyBand) =>
    b === 'limited' || b === 'not_attempted' ? 'var(--fg-2)' : 'var(--ink-0)';
  const segmentTextShadow = (b: TaskFamilyBand) =>
    b === 'limited' || b === 'not_attempted' ? undefined : '0 1px 2px color-mix(in oklab, var(--ink-900) 34%, transparent)';

  return (
    <TooltipProvider delayDuration={80}>
      <div>
        <div
          role="img"
          aria-label={segs
            .map((b) => `${countOf(b)} ${taskBandLabel(b).toLowerCase()}`)
            .join(', ')}
          style={{
            display: 'flex', height, borderRadius: 6, overflow: 'hidden',
            background: 'var(--ink-100)', border: '1px solid var(--border-subtle)',
            cursor: isInteractive ? 'pointer' : undefined,
          }}
        >
          {segs.filter((b) => countOf(b) > 0).map((b) => {
            const n = countOf(b);
            const clickable = isInteractive;
            const seg = clickable ? (
              <button
                type="button"
                className="task-family-distribution-segment"
                aria-label={`${taskBandLabel(b)}: ${n} students. Click to see list.`}
                onClick={() => onBandClick!(b, members![b])}
                style={{
                  flex: n,
                  height: '100%',
                  minWidth: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: 0,
                  padding: 0,
                  margin: 0,
                  appearance: 'none',
                  background: segmentFill(b),
                  outline: 'none',
                  cursor: 'pointer',
                  color: segmentTextColor(b),
                  fontFamily: 'var(--font-mono)',
                  fontSize: 12,
                  fontWeight: 900,
                  lineHeight: 1,
                  textShadow: segmentTextShadow(b),
                }}
              >
                <span
                  aria-hidden="true"
                  className="task-family-segment-count"
                  style={{ display: 'inline-flex', flexDirection: 'row', flexWrap: 'nowrap', alignItems: 'center', justifyContent: 'center', gap: 3, whiteSpace: 'nowrap' }}
                >
                  <span style={{ display: 'inline-block', lineHeight: 1 }}>{n}</span>
                  <Users className="task-family-segment-users-icon" size={10} aria-hidden="true" style={{ display: 'inline-block', flexShrink: 0, verticalAlign: 'middle' }} />
                </span>
              </button>
            ) : (
              <div
                style={{
                  flex: n,
                  height: '100%',
                  background: segmentFill(b),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: segmentTextColor(b),
                  fontFamily: 'var(--font-mono)',
                  fontSize: 12,
                  fontWeight: 900,
                  lineHeight: 1,
                  textShadow: segmentTextShadow(b),
                }}
              >
                <span
                  aria-hidden="true"
                  className="task-family-segment-count"
                  style={{ display: 'inline-flex', flexDirection: 'row', flexWrap: 'nowrap', alignItems: 'center', justifyContent: 'center', gap: 3, whiteSpace: 'nowrap' }}
                >
                  <span style={{ display: 'inline-block', lineHeight: 1 }}>{n}</span>
                  <Users className="task-family-segment-users-icon" size={10} aria-hidden="true" style={{ display: 'inline-block', flexShrink: 0, verticalAlign: 'middle' }} />
                </span>
              </div>
            );
            return clickable ? (
              <Tooltip key={b}>
                <TooltipTrigger asChild>{seg}</TooltipTrigger>
                <TooltipContent style={{ maxWidth: 220 }}>
                  <BandTooltipBody band={b} n={n} pct={pctOf(b)} />
                </TooltipContent>
              </Tooltip>
            ) : (
              <React.Fragment key={b}>{seg}</React.Fragment>
            );
          })}
        </div>

      </div>
    </TooltipProvider>
  );
}

interface RiskPyramidProps {
  counts: RiskCounts;
}

/* ─── Class risk profile bars: count text outside so bar length stays proportional ── */
function RiskPyramid({ counts }: RiskPyramidProps) {
  const isMobile = useMediaQuery('(max-width: 640px)');
  const tiers = [
    { k: 'low'      as const, n: counts.low, color: RISK.low.color, label: domainRiskLabel('low') },
    { k: 'moderate' as const, n: counts.moderate, color: RISK.moderate.color, label: domainRiskLabel('moderate') },
    { k: 'high'     as const, n: counts.high, color: RISK.high.color, label: domainRiskLabel('high') },
    { k: 'more_info' as const, n: counts.more_info || 0, color: 'var(--ink-150)', label: domainRiskLabel('more_info') },
  ];
  const maxN = Math.max(...tiers.map((tier) => tier.n), 1);

  interface TierBarProps {
    color: string;
    label: string;
    n: number;
    muted?: boolean;
  }

  function TierBar({ color, label, n, muted = false }: TierBarProps) {
    const pct = `${(Math.max(0, n) / maxN) * 100}%`;
    return (
      <div
        className="risk-pyramid-bar"
        style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? 'minmax(112px, 0.9fr) 56px minmax(120px, 1.4fr)' : '176px 74px minmax(0, 1fr)',
          gap: isMobile ? 10 : 16,
          alignItems: 'center',
          width: '100%',
          minHeight: isMobile ? 38 : 42,
        }}
      >
        <span style={{
          fontSize: isMobile ? 11 : 12,
          fontWeight: 800,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          lineHeight: 1.2,
          color: muted ? 'var(--fg-2)' : color,
          whiteSpace: 'nowrap',
        }}>{label}</span>
        <span style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 5,
          color: muted ? 'var(--fg-2)' : color,
        }} aria-label={`${n} students`}>
          <span style={{ fontSize: isMobile ? 20 : 24, fontFamily: 'var(--font-mono)', fontWeight: 900, lineHeight: 1 }}>{n}</span>
          <Users className="risk-pyramid-users-icon" size={isMobile ? 14 : 16} aria-hidden="true" />
        </span>
        <span style={{
          position: 'relative',
          height: isMobile ? 20 : 24,
          borderRadius: 999,
          background: muted ? 'var(--ink-50)' : 'color-mix(in oklab, var(--ink-100) 58%, white)',
          border: muted ? '1px dashed var(--border-default)' : undefined,
          overflow: 'hidden',
          boxSizing: 'border-box',
        }}>
          {n > 0 ? (
            <span style={{ position: 'absolute', inset: '0 auto 0 0', width: pct, minWidth: 0, borderRadius: 999, background: color }} />
          ) : (
            <span style={{ position: 'absolute', top: 4, bottom: 4, left: 0, borderLeft: '2px solid var(--border-default)' }} />
          )}
        </span>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 8 : 9 }}>
      {tiers.map(t => (
        <TierBar key={t.k} color={t.color} label={t.label} n={t.n} muted={t.k === 'more_info'} />
      ))}
    </div>
  );
}

/* ─── Risk Triangle (alternative to the stacked-bar Pyramid) ──
   Renders a 3-tier triangle (high apex, moderate middle, low base) with
   pastel fills tied to the risk-band tokens, count numbers centred in
   each tier, and a legend underneath. */
function RiskTriangle({ counts }: RiskPyramidProps) {
  const tiers = [
    { k: 'high'     as const, n: counts.high },
    { k: 'moderate' as const, n: counts.moderate },
    { k: 'low'      as const, n: counts.low },
  ];

  // Geometry: triangle with apex at top (50, 4) and base 5..95 at y=95.
  // Slice heights are weighted so the top slice is taller than the middle
  // and base slices — that gives a wider area at the apex slice's
  // midpoint, large enough to hold a 2-digit count at the same font size
  // used by every other slice.
  const W = 100;
  const apexX = 50;
  const apexY = 4;
  const baseY = 95;
  const H = baseY - apexY;
  const baseLeft = 5;
  const baseRight = 95;
  const weights = [1.45, 1.0, 1.0];
  const totalWeight = weights.reduce((a, b) => a + b, 0);
  const sliceH = weights.map(w => (w / totalWeight) * H);
  const yBoundaries = [apexY];
  for (let i = 0; i < sliceH.length; i++) {
    yBoundaries.push((yBoundaries[i] ?? apexY) + (sliceH[i] ?? 0));
  }
  // Helper: x-edges at a given y on each side of the triangle.
  function edges(y: number): [number, number] {
    const t = (y - apexY) / H; // 0 at apex, 1 at base
    const xL = apexX - (apexX - baseLeft) * t;
    const xR = apexX + (baseRight - apexX) * t;
    return [xL, xR];
  }
  function slicePath(i: number): string {
    const yTop = yBoundaries[i] ?? apexY;
    const yBot = yBoundaries[i + 1] ?? baseY;
    const [tlx, trx] = edges(yTop);
    const [blx, brx] = edges(yBot);
    if (i === 0) {
      // Top slice is a true triangle (apex point + base of the slice)
      return `M ${apexX} ${apexY} L ${brx} ${yBot} L ${blx} ${yBot} Z`;
    }
    return `M ${tlx} ${yTop} L ${trx} ${yTop} L ${brx} ${yBot} L ${blx} ${yBot} Z`;
  }
  function midY(i: number): number {
    const yTop = yBoundaries[i] ?? apexY;
    const yBot = yBoundaries[i + 1] ?? baseY;
    // For the top slice, drop the label slightly below midpoint so it
    // sits in the wider lower portion of the apex slice.
    const f = i === 0 ? 0.62 : 0.5;
    return yTop + f * (yBot - yTop);
  }

  // Lighter pastel fill for each band — derived from the risk colour token
  // by mixing with white. We render two stacked layers: a soft fill +
  // strong text in the band's accent colour.
  function lighten(c: string): string {
    return `color-mix(in oklab, ${c} 35%, white)`;
  }

  const fontSize = 12;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
      <svg
        viewBox={`0 0 ${W} ${baseY + 4}`}
        style={{ width: '100%', maxWidth: 360, height: 'auto' }}
        role="img"
        aria-label="Risk distribution triangle"
      >
        {tiers.map((t, i) => (
          <path
            key={t.k}
            d={slicePath(i)}
            fill={lighten(RISK[t.k].color)}
            style={{ stroke: 'var(--ink-0)' }}
            strokeWidth={0.6}
          />
        ))}
        {tiers.map((t, i) => (
          <text
            key={`n-${t.k}`}
            x={apexX}
            y={midY(i)}
            textAnchor="middle"
            dominantBaseline="middle"
            fontFamily="var(--font-mono)"
            fontWeight={900}
            fontSize={fontSize}
            fill={RISK[t.k].color}
          >
            {t.n}
          </text>
        ))}
      </svg>
      <div style={{ display: 'flex', gap: 18, justifyContent: 'center', flexWrap: 'wrap', fontSize: 12, color: 'var(--fg-2)' }}>
        {tiers.map(t => (
          <span key={t.k} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 9, height: 9, borderRadius: '50%', background: RISK[t.k].color, display: 'inline-block' }} />
            {domainRiskLabel(t.k)}
          </span>
        ))}
      </div>
    </div>
  );
}

interface PopulationPositionBarProps {
  /** Composite score value (100 + 15·z). Null when not yet calibrated. */
  score: number | null;
  /** Credible-interval bounds on the same score scale. */
  intervalLow: number | null;
  /** Credible-interval bounds on the same score scale. */
  intervalHigh: number | null;
  /** Comparison-group values used to render the empirical density. */
  distributionValues: number[];
  /** Number of peers contributing to the comparison curve. */
  sampleSize: number;
  /** Active background distribution scope. */
  scopeKind: PositionScope;
  /** Human label for the chosen background distribution. */
  scopeLabel?: string;
  /** Optional caption rendered beneath the chart. */
  caption?: string;
  /** Visible explanatory note rendered beneath the chart. */
  scaleNote?: React.ReactNode;
  /** Enables the visible PR band key beneath the score curve. */
  bandCutoffNote?: string;
  bandCutoffs?: { veryLowMax: number; lowMax: number };
  /** Percentile rank against the same-year ENSSA reference population. */
  percentileRank?: number | null;
}

/* Student position visual.
   The curve is a standardised-score distribution, which preserves the
   bell/density shape. The interpretation remains percentile-rank based:
   the coloured score regions correspond to the PR15 and PR35 cutoffs, and
   the student's marker shows the score number only. */
function PopulationPositionBar({
  score,
  intervalLow,
  intervalHigh,
  distributionValues,
  sampleSize,
  scopeKind,
  scopeLabel,
  caption,
  scaleNote,
  bandCutoffNote,
  bandCutoffs,
  percentileRank,
}: PopulationPositionBarProps) {
  const isNarrow = useMediaQuery('(max-width: 640px)');
  const clipId = React.useId();
  const AXIS_MIN = STANDARD_SCORE_AXIS_MIN;
  const AXIS_MAX = STANDARD_SCORE_AXIS_MAX;
  const AXIS_RANGE = AXIS_MAX - AXIS_MIN;
  type BandKey = "very-low" | "low" | "on-track";
  interface BandSpec {
    key: BandKey;
    label: string;
    prLabel: string;
    from: number;
    to: number;
    bg: string;
    fg: string;
    stroke: string;
  }

  const veryLowMax = bandCutoffs?.veryLowMax ?? HIGH_BAND_MAX_INDEX;
  const lowMax = bandCutoffs?.lowMax ?? MODERATE_BAND_MAX_INDEX;
  const bands: ReadonlyArray<BandSpec> = [
    {
      key: "very-low",
      label: "Very Low",
      prLabel: "PR15 and below",
      from: AXIS_MIN,
      to: veryLowMax,
      bg: "var(--risk-concern-bg)",
      fg: "var(--risk-concern-fg)",
      stroke: "var(--risk-concern)",
    },
    {
      key: "low",
      label: "Low",
      prLabel: "PR16 to PR35",
      from: veryLowMax,
      to: lowMax,
      bg: "var(--risk-emerging-bg)",
      fg: "var(--risk-emerging-fg)",
      stroke: "var(--risk-emerging)",
    },
    {
      key: "on-track",
      label: "On Track",
      prLabel: "PR36 and above",
      from: lowMax,
      to: AXIS_MAX,
      bg: "var(--risk-on-track-bg)",
      fg: "var(--risk-on-track-fg)",
      stroke: "var(--risk-on-track)",
    },
  ] as const;

  const VB_W = 600;
  const PAD_TOP = 20;
  const CURVE_H = 88;
  const AXIS_Y = PAD_TOP + CURVE_H;
  const CI_Y = AXIS_Y - 12;
  const VB_H = AXIS_Y + 6;
  const scoreToX = (i: number) =>
    ((Math.max(AXIS_MIN, Math.min(AXIS_MAX, i)) - AXIS_MIN) / AXIS_RANGE) * VB_W;

  const hasScore = score != null && Number.isFinite(score);
  const roundedScore = hasScore ? Math.round(score!) : null;
  const roundedPr = percentileRank != null && Number.isFinite(percentileRank)
    ? Math.max(1, Math.min(99, Math.round(percentileRank)))
    : null;
  const intervalBounds =
    intervalLow != null &&
    intervalHigh != null &&
    Number.isFinite(intervalLow) &&
    Number.isFinite(intervalHigh)
      ? [Math.min(intervalLow, intervalHigh), Math.max(intervalLow, intervalHigh)] as const
      : null;
  const hasInterval = intervalBounds != null;
  const curvePath = buildDensityPath(distributionValues, AXIS_MIN, AXIS_MAX, VB_W, CURVE_H);
  const closedCurvePath =
    curvePath != null
      ? `${curvePath} L ${VB_W.toFixed(1)} ${CURVE_H.toFixed(1)} L 0 ${CURVE_H.toFixed(1)} Z`
      : null;
  const hasStableDistribution = sampleSize >= 2 && curvePath != null && closedCurvePath != null;
  const shouldClipBandsToCurve = hasStableDistribution && !isNarrow;
  const defaultScopeLabel = scopeKind === "population" ? "the ENSSA reference population" : "the selected group";
  const comparisonLabel = scopeLabel ?? defaultScopeLabel;

  return (
    <div>
      {!hasStableDistribution && (
        <div style={{ fontSize: 11, color: 'var(--fg-3)', marginBottom: 10, fontStyle: 'italic' }}>
          Too few peers to show a stable comparison curve.
        </div>
      )}

      <div style={{ position: 'relative' }}>
        <svg
          viewBox={`0 0 ${VB_W} ${VB_H}`}
          preserveAspectRatio="none"
          role="img"
          aria-label={
            hasScore
              ? `Standardised ENSSA score ${roundedScore}${roundedPr != null ? `, PR ${roundedPr}` : ""}, shown against ${comparisonLabel}, sample size ${sampleSize}${hasStableDistribution ? "" : ". Too few peers to show a stable comparison curve."}`
              : "Standardised ENSSA score distribution, student score not yet available"
          }
          style={{ display: 'block', width: '100%', height: VB_H + 4, overflow: 'visible' }}
        >
          {shouldClipBandsToCurve && (
            <defs>
              <clipPath id={clipId}>
                <path d={closedCurvePath!} transform={`translate(0 ${PAD_TOP})`} />
              </clipPath>
            </defs>
          )}

          <g clipPath={shouldClipBandsToCurve ? `url(#${clipId})` : undefined}>
            {bands.map((band) => {
              const x = scoreToX(band.from);
              const w = scoreToX(band.to) - x;
              return (
                <rect
                  key={`band-${band.key}`}
                  x={x}
                  y={PAD_TOP}
                  width={w}
                  height={CURVE_H}
                  fill={band.bg}
                  opacity={shouldClipBandsToCurve ? 1 : isNarrow ? 0.95 : 0.55}
                />
              );
            })}
          </g>

          <g clipPath={shouldClipBandsToCurve ? `url(#${clipId})` : undefined}>
            {bands.slice(0, -1).map((band) => (
              <line
                key={`cut-${band.key}`}
                x1={scoreToX(band.to)}
                x2={scoreToX(band.to)}
                y1={PAD_TOP}
                y2={AXIS_Y}
                stroke={band.stroke}
                strokeWidth={1}
                opacity={isNarrow ? 0.5 : 0.45}
              />
            ))}
          </g>

          {hasStableDistribution && !isNarrow && (
            <path
              d={curvePath}
              transform={`translate(0 ${PAD_TOP})`}
              fill="none"
              stroke={DISTRIBUTION_FILL}
              strokeWidth={1.75}
              strokeLinejoin="round"
            />
          )}

          <line
            x1={0}
            x2={VB_W}
            y1={AXIS_Y}
            y2={AXIS_Y}
            stroke={CHART_AXIS.line}
            strokeWidth={1}
          />

          {hasScore && (() => {
            const mx = scoreToX(score!);
            return (
              <g>
                {hasInterval && (
                  <g aria-hidden="true">
                    <line
                      x1={scoreToX(intervalBounds![0])}
                      x2={scoreToX(intervalBounds![1])}
                      y1={CI_Y}
                      y2={CI_Y}
                      stroke="var(--enssa-aubergine)"
                      strokeOpacity={0.5}
                      strokeWidth={isNarrow ? 2.5 : 2}
                      strokeLinecap="round"
                    />
                    {!isNarrow && (
                      <>
                        <line
                          x1={scoreToX(intervalBounds![0])}
                          x2={scoreToX(intervalBounds![0])}
                          y1={CI_Y - 4}
                          y2={CI_Y + 4}
                          stroke="var(--enssa-aubergine)"
                          strokeOpacity={0.65}
                          strokeWidth={1.5}
                          strokeLinecap="round"
                        />
                        <line
                          x1={scoreToX(intervalBounds![1])}
                          x2={scoreToX(intervalBounds![1])}
                          y1={CI_Y - 4}
                          y2={CI_Y + 4}
                          stroke="var(--enssa-aubergine)"
                          strokeOpacity={0.65}
                          strokeWidth={1.5}
                          strokeLinecap="round"
                        />
                      </>
                    )}
                  </g>
                )}
                <line
                  x1={mx}
                  x2={mx}
                  y1={PAD_TOP}
                  y2={hasInterval ? CI_Y : AXIS_Y}
                  stroke="var(--enssa-aubergine)"
                  strokeWidth={2}
                  strokeLinecap="round"
                />
              </g>
            );
          })()}
        </svg>

        {hasScore && (() => {
          const rawPct = (scoreToX(score!) / VB_W) * 100;
          const pillPct = Math.max(4, Math.min(96, rawPct));
          const dotSize = isNarrow ? 10 : 11;
          return (
            <>
              <div
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  left: `${rawPct}%`,
                  top: (hasInterval ? CI_Y : AXIS_Y) - dotSize / 2,
                  transform: 'translateX(-50%)',
                  width: dotSize,
                  height: dotSize,
                  borderRadius: '50%',
                  background: 'var(--enssa-aubergine)',
                  border: '1.5px solid var(--ink-0)',
                  boxShadow: 'var(--sh-1)',
                  pointerEvents: 'none',
                  boxSizing: 'border-box',
                }}
              />
              <div
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  left: `${pillPct}%`,
                  top: 0,
                  transform: 'translateX(-50%)',
                  background: 'var(--enssa-aubergine)',
                  color: 'var(--ink-0)',
                  padding: '2px 8px',
                  borderRadius: 999,
                  fontSize: 11,
                  fontWeight: 700,
                  fontFamily: 'var(--font-mono)',
                  lineHeight: 1.2,
                  whiteSpace: 'nowrap',
                  boxShadow: 'var(--sh-1)',
                  pointerEvents: 'none',
                }}
              >
                {roundedScore}
              </div>
            </>
          );
        })()}
      </div>

      {bandCutoffNote && (
        <div
          aria-label="Score band key"
          style={{
            display: 'flex',
            gap: 10,
            flexWrap: 'wrap',
            alignItems: 'center',
            marginTop: 12,
            fontSize: 12,
            lineHeight: 1.35,
            color: 'var(--fg-2)',
          }}
        >
          {bands.map((band) => (
            <span
              key={`key-${band.key}`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '4px 8px',
                borderRadius: 999,
                background: band.bg,
                color: band.fg,
                fontWeight: 700,
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: 9,
                  height: 9,
                  borderRadius: 999,
                  background: band.stroke,
                  flexShrink: 0,
                }}
              />
              {band.label}: {band.prLabel}
            </span>
          ))}
        </div>
      )}

      {scaleNote && (
        <div
          style={{
            marginTop: 12,
            color: 'var(--fg-2)',
            fontSize: 12,
            lineHeight: 1.55,
          }}
        >
          {scaleNote}
        </div>
      )}

      {caption && (
        <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, color: 'var(--fg-2)', lineHeight: 1.5 }}>{caption}</span>
        </div>
      )}
    </div>
  );
}

export { RiskBreakdownStackedBar, TaskBoxPlotPanel, TaskFamilyDistributionBar, RiskPyramid, RiskTriangle, PopulationPositionBar };
